import { makeAutoObservable } from "mobx";
import ApiManager from "../helpers/ApiManager";
import UploadStore from "./UploadStore";

/**
 * Call store for managing video call sessions and related data
 * Handles call creation, call-token management for agora, session notes, and call state
 */
class callStore {
  /**
   * Initialize the call store
   * Sets up MobX observables for reactive state management
   */
  constructor() {
    makeAutoObservable(this);
  }

  /** @type {Object|null} Current call session data */
  call = null;

  /** @type {Array} Call attachments and files */
  attachments = [];

  /** @type {string|null} Customer call authentication token for agora */
  customerCallToken = null;

  /** @type {Object|null} Customer screen resolution data */
  customerScreenResolution = null;

  /** @type {boolean} Whether tech end call modal is open */
  techEndCallModal = false;

  /** @type {string} Session notes text */
  sessionNotes = "";

  /** @type {boolean} Whether end call by customer modal is open */
  isOpenEndCallByCustomerForTech = false;

  /** @type {boolean} Whether end call by tech modal is open */
  isOpenEndCallByTechForCustomer = false;

  /** @type {boolean} Whether device torch is enabled */
  isTorchOn = false;

  /** @type {string|null} Current call session slug */
  slug = null;

  /** @type {Array} Call messages history */
  messages = [];

  /** @type {Object} Call list filters for table of sessions in dashboard page*/
  filters = {};

  /**
   * Set or update call list filters
   * Merges new filters with existing ones
   * @param {Object} filters - Filter object with key-value pairs
   * @returns {void}
   */
  setFilters(filters) {
    this.filters = { ...this.filters, ...filters };
  }

  /**
   * Remove a specific filter by key
   * @param {string} key - Filter key to remove
   * @returns {void}
   */
  removeFilter(key) {
    delete this.filters[key];
  }

  /**
   * Set the current call session slug
   * @param {string} slug - Call session identifier
   * @returns {void}
   */
  setSlug(slug) {
    this.slug = slug;
  }

  /**
   * Set the current call data
   * @param {Object} call - Call session object
   * @returns {void}
   */
  setCall(call) {
    this.call = call;
  }

  /**
   * Set device torch state
   * @param {boolean} value - Whether torch is enabled
   * @returns {void}
   */
  setIsTorchOn(value) {
    this.isTorchOn = value;
  }

  /**
   * Set session notes text
   * @param {string} notes - Session notes content
   * @returns {void}
   */
  setSessionNotes = (notes) => {
    this.sessionNotes = notes;
  }

  /**
   * Set end call by customer modal state
   * @param {boolean} value - Whether modal is open
   * @returns {void}
   */
  setIsOpenEndCallByCustomerForTech(value) {
    this.isOpenEndCallByCustomerForTech = value;
  }

  /**
   * Set tech end call modal state
   * @param {boolean} value - Whether modal is open
   * @returns {void}
   */
  setTechEndCallModal(value) {
    this.techEndCallModal = value;
  }

  /**
   * Set end call by tech modal state
   * @param {boolean} value - Whether modal is open
   * @returns {void}
   */
  setIsOpenEndCallByTechForCustomer(value) {
    this.isOpenEndCallByTechForCustomer = value;
  }

  /**
   * Set customer screen resolution data
   * @param {Object} resolution - Resolution object with width and height
   * @returns {void}
   */
  setCustomerScreenResolution(resolution) {
    this.customerScreenResolution = resolution;
  }

  /**
   * Set customer call authentication token for agora
   * @param {string} token - Authentication token for customer access
   * @returns {void}
   */
  setCustomerCallToken(token) {
    this.customerCallToken = token;
  }

  /**
   * Get customer call authentication token for agora
   * @returns {string|null} Customer call token or null
   */
  getCustomerCallToken() {
    return this.customerCallToken;
  }

  /**
   * Fetch paginated list of calls with current filters
   * Automatically handles pagination and URL state management
   * @async
   * @returns {Promise<Object>} API response with calls data and pagination info
   */
  async getCalls() {
    try {
      var query = '';
      if (this.filters && Object.keys(this.filters).length > 0) {
        query = `?${Object.entries(this.filters).map(([key, value]) => `${key}=${value}`).join('&')}`;
      }


      const response = await ApiManager.request(`calls${query}`, 'GET');
      if (response.success) {
        if (response.data.pagination.total > 0 && response.data.calls.length === 0) {
          this.setFilters({ page: 1 });
          return await this.getCalls();
        } else {
          window.history.replaceState({}, '', `${window.location.pathname}${query}`);
          return { ...response, path: query };
        }
      }
      else {
        return {
          success: false,
          message: response.message,
        };
      }
    } catch (error) {
      // console.log("🚀 ~ CallStore.js:193 ~ callStore ~ getCalls ~ error:", error);
      return {
        success: false,
        message: error.response.data.message,
      };
    }
  }

  /**
   * Create a new call session for a customer
   * @async
   * @param {string} customerNumber - Customer's phone number
   * @returns {Promise<Object>} API response with new call data
   */
  async createCall(customerNumber) {
    try {
      localStorage.removeItem('callToken');
      const response = await ApiManager.request('calls', 'POST', { customer: customerNumber });
      return response;

    } catch (error) {
      // console.log("🚀 ~ CallStore.js:212 ~ callStore ~ createCall ~ error:", error);
      return {
        success: false,
        message: error.response.data.message,
      };
    }
  }

  /**
   * Fetch detailed call information by slug
   * Updates local call state and messages
   * @async
   * @param {string} slug - Call session identifier
   * @returns {Promise<Object>} API response with call details
   */
  async getCall(slug) {
    if (!slug) {
      return;
    }

    try {
      const response = await ApiManager.request(`calls/${slug}`, 'GET');
      if (response.success) {
        this.call = response.data;

        this.messages = response.data.messages.map(message => ({
          ...message,
          message: {
            type: message.message.type === 'file' ? 'file' : 'text',
            content: message.message.type === 'file' ? JSON.parse(message.message.content) : message.message.content
          }
        }));

        UploadStore.setSessionNotesFiles(response.data.attachments);
      } else {
        return {
          success: false,
          message: response.message,
        };
      }
      return response;
    } catch (error) {
      // console.log("🚀 ~ CallStore.js:255 ~ callStore ~ getCall ~ error:", error);
      return {
        success: false,
        message: error.response.data.message,
      };
    }
  }

  /**
   * Update call session data
   * @async
   * @param {string} slug - Call session identifier
   * @param {Object} body - Update data object
   * @returns {Promise<Object>} API response
   */
  async updateCall(slug, body) {
    try {
      const response = await ApiManager.request(`calls/${slug}`, 'PUT', body);
      return response;
    } catch (error) {
      // console.log("🚀 ~ CallStore.js:276 ~ callStore ~ updateCall ~ error:", error);
      return {
        success: false,
        message: error.response.data.message,
      };
    }
  }

  /**
   * Get Agora call token for video communication and agora
   * Caches token in localStorage to avoid repeated requests
   * @async
   * @param {string} slug - Call session identifier
   * @param {boolean} force - Force refresh token from server
   * @returns {Promise<Object>} API response with token
   */
  async getCallToken(slug, force = false) {
    try {
      const storageToken = localStorage.getItem('callToken');
      if (storageToken && !force) {
        return {
          success: true,
          data: storageToken
        }
      }
      const response = await ApiManager.request(`calls/${slug}/token`, 'GET');
      localStorage.setItem('callToken', response.data);
      return response;
    } catch (error) {
      // console.log("🚀 ~ CallStore.js:295 ~ callStore ~ getCallToken ~ error:", error);
      return {
        success: false,
        message: error.response.data.message,
      };
    }

  }

  /**
   * End call session by technician
   * @async
   * @param {string} callId - Call session ID
   * @param {Object} body - End call data (issue status, notes, etc.)
   * @returns {Promise<Object>} API response
   */
  async endCallByTech(callId, body) {
    try {
      const response = await ApiManager.request(`calls/${callId}/end`, 'POST', body);
      return response;
    } catch (error) {
      // console.log("🚀 ~ CallStore.js:326 ~ callStore ~ endCallByTech ~ error:", error);
      return {
        success: false,
        message: error.response.data.message,
      };
    }
  }

  /**
   * Rate service quality by technician
   * @async
   * @param {string} slug - Call session identifier
   * @param {Object} body - Rating data
   * @returns {Promise<Object>} API response
   */
  async rateService(slug, body) {
    try {
      const response = await ApiManager.request(`calls/${slug}/rate/technician`, 'POST', body);
      return response;

    } catch (error) {
      // console.log("🚀 ~ CallStore.js:345 ~ callStore ~ rateService ~ error:", error);
      return {
        success: false,
        message: error.response.data.message,
      };
    }
  }

  /**
   * Send customer review and rating
   * @async
   * @param {Object} body - Review data
   * @returns {Promise<Object>} API response
   */
  async sendCustomerReview(body) {
    try {
      const response = await ApiManager.request(`public/call/rate`, 'POST', body);
      return response;
    } catch (error) {
      // console.log("🚀 ~ CallStore.js:366 ~ callStore ~ sendCustomerReview ~ error:", error);
      return {
        success: false,
        message: error.response.data.message,
      };
    }
  }

  /**
   * Get legal information for customer
   * privacy and terms of service and accept the relevant legal notice
   * @async
   * @returns {Promise<Object>} API response with legal information
   */
  async getLegalForCustomer(slug) {
    try {
      const response = await ApiManager.request(`public/${slug}/legal`, 'GET');
      return response;
    } catch (error) {
      // console.log("🚀 ~ CallStore.js:383 ~ callStore ~ getLegalForCustomer ~ error:", error);
      return {
        success: false,
        message: error.response.data.message,
      };
    }
  }

}

const CallStore = new callStore();

export default CallStore;